// ApiAccessService.js

import { getAuth } from 'firebase/auth';

function cleanString(value) {
    return String(value || '').trim();
}

function resolveBaseApiBase() {
    const configured = cleanString(
      process.env.REACT_APP_BASE_API_BASE_URL
      || process.env.REACT_APP_WORKSPACE_API_BASE_URL,
    ).replace(/\/+$/, '');
    if (configured) return configured;

    return '';
}

function getBasePath(baseId) {
    const cleanBaseId = cleanString(baseId);
    if (!cleanBaseId) {
        throw new Error('baseId is required.');
    }

    const encodedBaseId = encodeURIComponent(cleanBaseId);
    const path = `/v1/bases/${encodedBaseId}`;
    const baseApiBase = resolveBaseApiBase();
    if (!baseApiBase) return path;
    return `${baseApiBase}${path}`;
}

function parseJson(text) {
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch (_error) {
        return null;
    }
}

function resolveApiErrorMessage(payload, fallbackMessage) {
    if (payload && typeof payload.error === 'object' && typeof payload.error.message === 'string') {
        const message = cleanString(payload.error.message);
        if (message) return message;
    }

    if (payload && typeof payload.message === 'string') {
        const message = cleanString(payload.message);
        if (message) return message;
    }

    return fallbackMessage;
}

function buildApiError(status, payload, fallbackMessage) {
    const error = new Error(resolveApiErrorMessage(payload, fallbackMessage));
    error.status = status;
    error.payload = payload;
    return error;
}

async function resolveBearerToken(overrideToken) {
    const explicitToken = cleanString(overrideToken);
    if (explicitToken) return explicitToken;

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
        throw new Error('You must be signed in to call the base API.');
    }

    return user.getIdToken();
}

async function requestBaseApi({
    baseId,
    path,
    method = 'GET',
    body,
    bearerToken,
    headers = {},
    includeMeta = false,
}) {
    const cleanPath = cleanString(path);
    if (!cleanPath || !cleanPath.startsWith('/')) {
        throw new Error('Base API path must start with "/".');
    }

    const token = await resolveBearerToken(bearerToken);
    const url = `${getBasePath(baseId)}${cleanPath}`;

    let response;
    try {
        response = await fetch(url, {
            method,
            headers: {
                Authorization: `Bearer ${token}`,
                ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
                ...headers,
            },
            body: body === undefined ? undefined : JSON.stringify(body),
        });
    } catch (networkError) {
        const message = cleanString(networkError?.message) || 'Failed to fetch';
        throw new Error(
            `Network error calling Base API at ${url}: ${message}. `
            + 'Set REACT_APP_BASE_API_BASE_URL (or legacy REACT_APP_WORKSPACE_API_BASE_URL) to your deployed API origin, or run the local API runtime.',
        );
    }

    const text = await response.text();
    const payload = parseJson(text);

    if (!response.ok) {
        throw buildApiError(response.status, payload, `Base API request failed (${response.status}).`);
    }

    if (!includeMeta && payload === null && cleanString(text)) {
        const looksLikeHtml = /^\s*</.test(text);
        const fallbackMessage = looksLikeHtml
            ? 'Base API returned HTML instead of JSON. Check REACT_APP_BASE_API_BASE_URL and backend routing.'
            : 'Base API returned a non-JSON response.';

        throw buildApiError(response.status, payload, fallbackMessage);
    }

    if (includeMeta) {
        return {
            status: response.status,
            headers: Object.fromEntries(response.headers.entries()),
            body: payload,
            text,
        };
    }

    return payload;
}

export async function listBaseServiceAccounts({ baseId }) {
    return requestBaseApi({
        baseId,
        path: '/service-accounts',
        method: 'GET',
    });
}

export async function createBaseType({
    baseId,
    label,
    labelPlural,
    description,
    iconKey,
    defaultView,
    schema,
}) {
    return requestBaseApi({
        baseId,
        path: '/types',
        method: 'POST',
        body: {
            label,
            labelPlural,
            description,
            iconKey,
            defaultView,
            schema,
        },
    });
}

export async function listBaseWebhookRules({ baseId }) {
    return requestBaseApi({
        baseId,
        path: '/webhooks/rules',
        method: 'GET',
    });
}

export async function createBaseWebhookRule({
    baseId,
    name,
    typeId,
    event = 'node.created',
    webhookId,
    destinationUrl,
    destinationName,
}) {
    return requestBaseApi({
        baseId,
        path: '/webhooks/rules',
        method: 'POST',
        body: {
            name,
            typeId,
            event,
            webhookId,
            destinationUrl,
            destinationName,
        },
    });
}

export async function updateBaseWebhookRule({ baseId, ruleId, patch }) {
    const id = cleanString(ruleId);
    if (!id) throw new Error('ruleId is required.');

    return requestBaseApi({
        baseId,
        path: `/webhooks/rules/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: patch,
    });
}

export async function updateBaseWebhookEndpoint({ baseId, webhookId, patch }) {
    const id = cleanString(webhookId);
    if (!id) throw new Error('webhookId is required.');

    return requestBaseApi({
        baseId,
        path: `/webhooks/endpoints/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: patch,
    });
}

export async function deleteBaseWebhookRule({ baseId, ruleId }) {
    const id = cleanString(ruleId);
    if (!id) throw new Error('ruleId is required.');

    return requestBaseApi({
        baseId,
        path: `/webhooks/rules/${encodeURIComponent(id)}`,
        method: 'DELETE',
    });
}

export async function testBaseWebhookRule({ baseId, ruleId, payload }) {
    const id = cleanString(ruleId);
    if (!id) throw new Error('ruleId is required.');

    return requestBaseApi({
        baseId,
        path: `/webhooks/rules/${encodeURIComponent(id)}/test`,
        method: 'POST',
        body: payload === undefined ? {} : { payload },
    });
}

export async function listBaseIncomingHooks({ baseId }) {
    return requestBaseApi({
        baseId,
        path: '/incoming/hooks',
        method: 'GET',
    });
}

export async function createBaseIncomingHook({
    baseId,
    name,
    typeId,
    action = 'create',
}) {
    return requestBaseApi({
        baseId,
        path: '/incoming/hooks',
        method: 'POST',
        body: {
            name,
            typeId,
            action,
        },
    });
}

export async function updateBaseIncomingHook({ baseId, hookId, patch }) {
    const id = cleanString(hookId);
    if (!id) throw new Error('hookId is required.');

    return requestBaseApi({
        baseId,
        path: `/incoming/hooks/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: patch,
    });
}

export async function deleteBaseIncomingHook({ baseId, hookId }) {
    const id = cleanString(hookId);
    if (!id) throw new Error('hookId is required.');

    return requestBaseApi({
        baseId,
        path: `/incoming/hooks/${encodeURIComponent(id)}`,
        method: 'DELETE',
    });
}

export async function createBaseServiceAccount({ baseId, name, role, scopes, permissions }) {
    return requestBaseApi({
        baseId,
        path: '/service-accounts',
        method: 'POST',
        body: {
            name,
            role,
            scopes,
            permissions,
        },
    });
}

export async function rotateBaseServiceAccountToken({ baseId, serviceAccountId }) {
    const id = cleanString(serviceAccountId);
    if (!id) throw new Error('serviceAccountId is required.');

    return requestBaseApi({
        baseId,
        path: `/service-accounts/${encodeURIComponent(id)}/rotate-token`,
        method: 'POST',
    });
}

export async function revokeBaseServiceAccount({ baseId, serviceAccountId }) {
    const id = cleanString(serviceAccountId);
    if (!id) throw new Error('serviceAccountId is required.');

    return requestBaseApi({
        baseId,
        path: `/service-accounts/${encodeURIComponent(id)}/revoke`,
        method: 'POST',
    });
}

export async function deleteBaseServiceAccount({ baseId, serviceAccountId }) {
    const id = cleanString(serviceAccountId);
    if (!id) throw new Error('serviceAccountId is required.');

    try {
        return await requestBaseApi({
            baseId,
            path: `/service-accounts/${encodeURIComponent(id)}`,
            method: 'DELETE',
        });
    } catch (error) {
        const status = Number(error?.status || 0);
        const message = cleanString(error?.message).toLowerCase();
        const shouldFallback = status === 404 && message.includes('route not found');

        if (!shouldFallback) {
            throw error;
        }

        return requestBaseApi({
            baseId,
            path: `/service-accounts/${encodeURIComponent(id)}/delete`,
            method: 'POST',
        });
    }
}

export async function updateBaseServiceAccountConnectionPolicy({
    baseId,
    serviceAccountId,
    enabled,
    readRpm,
    writeRpm,
    dataVisibility,
    notes,
    targetSystem,
    templateId,
}) {
    const id = cleanString(serviceAccountId);
    if (!id) throw new Error('serviceAccountId is required.');

    return requestBaseApi({
        baseId,
        path: `/service-accounts/${encodeURIComponent(id)}/connection-policy`,
        method: 'POST',
        body: {
            enabled,
            readRpm,
            writeRpm,
            dataVisibility,
            notes,
            targetSystem,
            templateId,
        },
    });
}

export async function getBaseApiLimits({ baseId }) {
    return requestBaseApi({
        baseId,
        path: '/limits',
        method: 'GET',
    });
}

export async function getBaseOpenApiSpec({ baseId }) {
    return requestBaseApi({
        baseId,
        path: '/openapi.json',
        method: 'GET',
    });
}

export async function getBaseSchemaDoc({ baseId }) {
    return requestBaseApi({
        baseId,
        path: '/schema.json',
        method: 'GET',
    });
}

export async function runApiTestRequest({
    baseId,
    bearerToken,
    path,
    method = 'GET',
    body,
}) {
    const normalizedMethod = cleanString(method || 'GET').toUpperCase();
    const withBody = ['POST', 'PATCH', 'PUT'].includes(normalizedMethod);

    return requestBaseApi({
        baseId,
        path,
        method: normalizedMethod,
        body: withBody ? body : undefined,
        bearerToken,
        includeMeta: true,
    });
}
